/**
 * Dependencies.
 */
import { FormGroup } from '@prz-dp/services';
import { Component, Mixins } from 'vue-property-decorator';

import { addressReverseService } from '@/api/address/reverse.service';
import SearchFormModel from '@/api/address/search-form.model';
import AddressSearchModel from '@/api/address/search.model';
import { addressSearchService } from '@/api/address/search.service';
import { servicePointService } from '@/api/address/service-point.service';
import { formatAddressSingleLine } from '@/modules/main/utils/address';
import CountryUtils from '@/modules/main/utils/country';
import { removeEmojis } from '@/modules/main/utils/helpers';
import { TRACKING_ERRORS } from '@/modules/tracking/constants';
import TimelineMixin from '@/modules/tracking/mixins/timeline.mixin';
import TrackingMixin from '@/modules/tracking/mixins/tracking.mixin';
import { trackingStore } from '@/modules/tracking/store';
import Error from '@/ui/components/error.component.vue';
import NotifyClientRequest from '@/ui/components/notify/client-request.component.vue';
import Timeline from '@/ui/components/timeline/timeline.component.vue';

/**
 * Component.
 */
@Component({
  components: { Error, NotifyClientRequest, Timeline },
  name: 'search-address-scene'
})
export default class extends Mixins(TrackingMixin, TimelineMixin) {
  /**
   * Property `addresses`.
   */
  addresses = null;

  /**
   * Property `formGroup`.
   */
  formGroup: FormGroup = null;

  /**
   * Property `hasSearchCriteria`.
   */
  hasSearchCriteria = false;

  /**
   * Property `coordinates`.
   */
  coordinates = null;

  /**
   * Property `isUsingCurrentLocation`.
   */
  isUsingCurrentLocation = false;

  /**
   * Property `isAddressValidated`.
   */
  isAddressValidated = false;

  /**
   * Property `hasSubmittedSearch`.
   */
  hasSubmittedSearch = false;

  /**
   * Property `hasServicePoints`.
   */
  hasServicePoints = false;

  /**
   * Getter `address`.
   */
  get address() {
    const { order = null } = trackingStore || {};
    const { address = null } = order || {};

    return address;
  }

  /**
   * Getter `servicePoint`.
   */
  get servicePoint() {
    const { order = null } = trackingStore || {};
    const { address = null } = order || {};
    const { servicePoint = null } = address || {};
    const { name = null } = servicePoint || {};

    return name;
  }

  /**
   * Getter `currentAddressFormatted`.
   */
  get currentAddressFormatted() {
    return formatAddressSingleLine({ address: this.address });
  }

  /**
   * Getter zip code mask.
   */
  get zipCodeMask() {
    const { blocks, delimiter, mask } = CountryUtils.zipCodeMask || {};

    return { blocks, delimiter, mask };
  }

  /**
   * Getter can show pick up point option.
   */
  get canShowPickUpPointOption() {
    return (
      this.canChangeAddressToServicePoint &&
      this.hasServicePoints &&
      !this.hasSearchCriteria &&
      !this.isUsingCurrentLocation
    );
  }

  /**
   * Getter `numberPackages`.
   */
  get numberPackages() {
    const { numberPackages = null } = trackingStore || {};

    return numberPackages;
  }

  /**
   * Getter `expectedOrderDate`.
   */
  get expectedOrderDate() {
    let expectedOrderDate = null;

    if (this.isDelivery) {
      const { expectedDeliveryDate = null } = trackingStore.delivery || {};
      expectedOrderDate = expectedDeliveryDate;
    } else {
      const { expectedCollectDate = null } = trackingStore.collect || {};
      expectedOrderDate = expectedCollectDate;
    }

    return expectedOrderDate;
  }

  /**
   * Getter `newDate`.
   */
  get newDate() {
    const { dayChanged } = trackingStore || {};

    return dayChanged || null;
  }

  /**
   * Lifecycle created.
   */
  created() {
    this.formGroup = this.$kit.forms.generateFormGroup(new SearchFormModel({}));
  }

  /**
   * Lifecycle mounted hook.
   */
  async mounted() {
    this.$on('trackingDataUpdated', () => {
      const isValid = this.handleIsValidToBeOnScene();

      if (!isValid) {
        this.handleForceTrackingScene();
      }
    });

    await this.handleInitializeMixin();

    const isValid = this.handleIsValidToBeOnScene();

    if (!isValid) {
      this.handleForceTrackingScene();
    } else {
      await this.handleInitialize();
    }
  }

  /**
   * Handle is valid to be on scene.
   */
  handleIsValidToBeOnScene() {
    return this.canCreateChangeRequest;
  }

  /**
   * Handle initialize.
   */
  async handleInitialize() {
    this.isLoading = true;

    await this.handleGetServicePoints();

    this.isLoading = false;
  }

  /**
   * Handle get service points.
   */
  async handleGetServicePoints() {
    try {
      let { servicePoints = [] } = trackingStore || {};

      if (!servicePoints?.length) {
        servicePoints = await servicePointService.get();
        servicePoints = this.handleFilterServicePoints(servicePoints);
        trackingStore.setServicePoints(servicePoints);
      }

      const { length = 0 } = servicePoints || [];

      this.hasServicePoints = !!length;
    } catch (error) {
      if (process.env.BUILD_MODE !== 'prd') {
        console.error({
          error,
          method: 'handleGetServicePoints',
          scene: 'SearchAddressScene'
        });
      }
    }
  }

  /**
   * Handle get coordinates.
   */
  handleGetCoordinates() {
    if (navigator.geolocation) {
      this.isLoading = true;

      this.formGroup = this.$kit.forms.generateFormGroup(
        new SearchFormModel({})
      );

      // Ask for permission.
      navigator.geolocation.getCurrentPosition(
        async position => {
          const { coords = null } = position || {};
          const { latitude = null, longitude = null } = coords || {};

          this.coordinates = {
            latitude,
            longitude
          };

          let query = '';

          query = `${query}${
            latitude ? `?latitude=${encodeURIComponent(latitude)}` : ''
          }`;
          query = `${query}${
            longitude ? `&longitude=${encodeURIComponent(longitude)}` : ''
          }`;
          query = `${query}${
            this.trackingNumber
              ? `&trackingNumber=${encodeURIComponent(this.trackingNumber)}`
              : ''
          }`;
          query = `${query}&orderType=${encodeURIComponent(
            this.isDelivery ? 'DELIVERY' : 'COLLECT'
          )}`;

          this.isLoading = true;

          try {
            // Search addresses.
            const response = await addressReverseService.GET(
              `${addressReverseService.getModelEndpoint}${query}`
            );

            if (!!latitude && !!longitude) {
              this.isAddressValidated = true;
            }

            if (response) {
              this.addresses = [
                {
                  ...response,
                  latitude,
                  longitude
                }
              ];
            }
          } catch (error) {
            const { raw = null } = error || {};
            const { code = null } = raw || {};

            this.addresses = [];

            if (code === TRACKING_ERRORS.ZIP_CODE_OUT_OF_RANGE) {
              this.$router.push({
                name: 'OutOfRange',
                params: {
                  key: this.key
                }
              });
            }

            if (process.env.BUILD_MODE !== 'prd') {
              console.error(error);
            }
          } finally {
            this.isLoading = false;
            this.isUsingCurrentLocation = true;
          }
        },
        () => {
          this.addresses = [];
          this.isLoading = false;
          this.isUsingCurrentLocation = true;
        },
        { enableHighAccuracy: true, maximumAge: 0, timeout: 5000 }
      );
    }
  }

  /**
   * Handle search criteria.
   */
  handleEditSearchCriteria() {
    this.addresses = null;

    this.hasSearchCriteria = false;
    this.isUsingCurrentLocation = false;
  }

  /**
   * Handle search address.
   */
  async handleSearchAddress() {
    let street: string = this.formGroup.get('street').value;
    let doorNumber: string = this.formGroup.get('doorNumber').value;
    let city: string = this.formGroup.get('city').value;
    let zipCode: string = this.formGroup.get('zipCode').value;

    street = removeEmojis(street);
    doorNumber = removeEmojis(doorNumber);
    city = removeEmojis(city);
    zipCode = removeEmojis(zipCode);

    this.formGroup.get('street').setValue(street);
    this.formGroup.get('doorNumber').setValue(doorNumber);
    this.formGroup.get('city').setValue(city);
    this.formGroup.get('zipCode').setValue(zipCode);

    const valid = await this.formGroup.validateForm();

    this.hasSubmittedSearch = true;

    // Valid search form.
    if (valid) {
      this.hasSearchCriteria = true;

      let query = `?street=${encodeURIComponent(street)}`;
      query = `${query}&doorNumber=${encodeURIComponent(doorNumber)}`;
      query = `${query}&zipCode=${encodeURIComponent(zipCode)}`;
      query = `${query}&city=${encodeURIComponent(city)}`;
      query = `${query}&trackingNumber=${encodeURIComponent(
        this.trackingNumber
      )}`;
      query = `${query}&orderType=${encodeURIComponent(
        this.isDelivery ? 'DELIVERY' : 'COLLECT'
      )}`;

      this.isLoading = true;

      try {
        // Search addresses.
        const response = await addressSearchService.GET(
          `${addressSearchService.getModelEndpoint}${query}`
        );

        const { addresses = [], isValidated = false } =
          (response as AddressSearchModel) || {};

        this.isAddressValidated = isValidated;
        this.addresses = addresses.map(
          ({
            additionalInformation = null,
            city = null,
            countryCode = null,
            countryName = null,
            doorNumber = null,
            latitude = null,
            longitude = null,
            street = null,
            zipCode = null
          }) => ({
            additionalInformation,
            city,
            countryCode,
            countryName,
            doorNumber,
            latitude,
            longitude,
            street,
            zipCode
          })
        );
      } catch (error) {
        const { raw = null } = error || {};
        const { code = null } = raw || {};

        this.addresses = [];

        if (code === TRACKING_ERRORS.ZIP_CODE_OUT_OF_RANGE) {
          this.$router.push({
            name: 'OutOfRange',
            params: {
              key: this.key
            }
          });
        }
      } finally {
        this.isLoading = false;
      }
    }
  }

  /**
   * Handle choose address.
   */
  handleChooseAddress({ address }) {
    trackingStore.setChangeDeliveryAddress({
      address: {
        ...address,
        isValidated: this.isAddressValidated
      }
    });

    this.$router.push({
      name: 'ChangeAddress',
      params: {
        key: this.key
      }
    });
  }

  /**
   * Handle action button.
   */
  handleActionButton() {
    if (this.hasSearchCriteria || this.isUsingCurrentLocation) {
      this.handleEditSearchCriteria();
    } else {
      this.handleSearchAddress();
    }
  }

  /**
   * Handle back or cancel.
   */
  handleBackOrCancel() {
    if (this.hasSearchCriteria || this.isUsingCurrentLocation) {
      this.$router.push({
        name: 'Tracking',
        params: {
          key: this.key
        }
      });
    } else {
      this.$router.back();
    }
  }

  /**
   * Handle scroll to.
   */
  handleScrollTo({ elementName }) {
    const element = this.$refs[elementName] as HTMLElement;
    const topPosition = element.offsetTop;
    const elementContent = document.getElementsByClassName(
      'app'
    ) as HTMLCollection;

    setTimeout(() => {
      elementContent.item(0).scrollTop = topPosition - 48;
    }, 500);
  }

  /**
   * Handle zip code formatted.
   */
  async handleZipCodeFormatted({ target = null }) {
    const { dataset = null } = target || {};
    const {
      formatedValue = null
    }: {
      formatedValue: string;
      rawValue: string;
    } = dataset || {};

    await this.formGroup.get('zipCode').setValue(formatedValue);
  }

  /**
   * Handle pick up points.
   */
  handleViewPickUpPoints() {
    const { key } = this;

    this.$router.push({
      name: 'PickUpList',
      params: { key }
    });
  }
}
